'use client';

import { Swiper, SwiperSlide } from 'swiper/react';
import { Keyboard, Mousewheel, Scrollbar, Autoplay } from 'swiper/modules';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import CursorTooltip from './CursorTooltip';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/scrollbar';

interface CarouselScaleProps {
  images: string[];
  alt?: string;
  className?: string;
  autoplay?: boolean;
  autoplayDelay?: number;
  scrollbar?: boolean;
  initialSlide?: number;
}

/**
 * CarouselScale - A Swiper carousel component with centered slides and larger center image
 * 
 * Features:
 * - Centered slides with larger center image
 * - Scrollbar navigation
 * - Responsive full-width design
 * - Keyboard navigation support
 * - Mouse wheel support
 * - Touch/swipe support
 * - Accessible with proper ARIA labels
 * 
 * @component
 * @example
 * ```tsx
 * <CarouselScale 
 *   images={['/image1.jpg', '/image2.jpg', '/image3.jpg']}
 *   alt="Case study images"
 * />
 * ```
 */
export default function CarouselScale({
  images,
  alt = 'Carousel image',
  className = '',
  autoplay = false,
  autoplayDelay = 3000,
  scrollbar = true,
  initialSlide
}: CarouselScaleProps) {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const handleDragStart = () => {
    // Hide tooltip with CSS to avoid re-renders
    // The tooltip is portaled to document.body, so we find it there
    const tooltip = document.body.querySelector('[role="tooltip"]') as HTMLElement;
    if (tooltip) {
      tooltip.style.display = 'none';
    }
  };

  if (!isMounted || images.length === 0) {
    return null;
  }

  const modules = [Keyboard, Mousewheel];
  if (autoplay) {
    modules.push(Autoplay);
  }

  return (
    <div 
      className={`carousel-scale overflow-hidden ${className}`}
      role="region"
      aria-label="Image carousel"
    >
      <CursorTooltip tooltipText="Drag" backgroundColor="#FACC15" textColor="#000000">
        <div onMouseDown={handleDragStart}>
          <Swiper
            modules={modules}
            centeredSlides={true}
            slidesPerView="auto"
            spaceBetween={60}
            initialSlide={initialSlide !== undefined ? initialSlide : (images.length > 1 ? 1 : 0)}
            grabCursor={true}
            keyboard={{
              enabled: true,
            }}
            mousewheel={{
              forceToAxis: true,
              sensitivity: 1,
              releaseOnEdges: true,
            }}
            onTouchStart={handleDragStart}
            onSliderMove={handleDragStart}
            autoplay={autoplay ? {
              delay: autoplayDelay,
              disableOnInteraction: false,
            } : false}
            breakpoints={{
              320: {
                slidesPerView: 'auto',
                spaceBetween: 40,
              },
              768: {
                slidesPerView: 'auto',
                spaceBetween: 70,
              },
              1024: {
                slidesPerView: 'auto',
                spaceBetween: 85,
              },
            }}
            className="w-full"
            aria-label="Image carousel"
          >
          {images.map((image, index) => (
            <SwiperSlide 
              key={`${image}-${index}`}
              className="carousel-scale-slide"
            >
              <div className="relative aspect-[5/4] overflow-hidden">
                <Image
                  src={image}
                  alt={`${alt} ${index + 1}`}
                  fill
                  sizes="(max-width: 640px) 85vw, (max-width: 1024px) 60vw, 50vw"
                  className="object-cover transition-transform duration-300"
                  priority={index === 0}
                />
              </div>
            </SwiperSlide>
          ))}
          </Swiper>
        </div>
      </CursorTooltip>

      <style jsx global>{`
        .carousel-scale .swiper {
          overflow: visible;
          padding: 3.75rem 0 3.75rem 0;
        }

        .carousel-scale-slide {
          width: 15rem !important;
          transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }

        @media (min-width: 768px) {
          .carousel-scale-slide {
            width: 25rem !important;
          }
        }

        @media (min-width: 1024px) {
          .carousel-scale-slide {
            width: 31.25rem !important;
          }
        }

        .carousel-scale-slide.swiper-slide-active {
          transform: scale(1.3);
          z-index: 10;
        }

      `}</style>
    </div>
  );
}

